import { mapSidebarSections } from "@/features/experience-shared/utils/map-sidebar-sections";
import { SidebarSections, SidebarCategory } from "@/types/DocumentSidebar";
import { Icon } from "@/ui-kit/NovaIcon/NovaIcon.vue";
import { ExperienceRevision, RevisionOptions } from "../types/revision";
import { RevisionFormProps } from "../types/forms";
import RevisionLocationForm from "@/features/experience-revision/components/RevisionLocationForm.vue";
import RevisionSettingsForm from "@/features/experience-revision/components/RevisionSettingsForm.vue";
import RevisionContentSegmentationForm from "@/features/experience-revision/components/RevisionContentSegmentationForm.vue";
import RevisionCustomerInfoForm from "@/features/experience-revision/components/RevisionCustomerInfoForm.vue";
import RevisionDistributionTagsForm from "@/features/experience-revision/components/RevisionDistributionTagsForm.vue";
import RevisionOperationalForm from "@/features/experience-revision/components/RevisionOperationalForm.vue";
import RevisionOptionNamesForm from "@/features/experience-revision/components/RevisionOptionNamesForm.vue";
import { useFeatureFlag } from "@/features/experience-shared/composables/useFeatureFlag";

export function getCurationForms(
  values: ExperienceRevision | null,
  options: RevisionOptions,
  requiredFields: string[]
): Record<string, { is: unknown; props: RevisionFormProps }> {
  const curationFormProps: RevisionFormProps = {
    values,
    options,
    requiredFields,
    flow: "curation",
  } as const;

  const forms: Record<string, { is: unknown; props: RevisionFormProps }> = {
    settings: {
      is: RevisionSettingsForm,
      props: curationFormProps,
    },
    location: {
      is: RevisionLocationForm,
      props: curationFormProps,
    },
    "content-segmentation": {
      is: RevisionContentSegmentationForm,
      props: curationFormProps,
    },
    "customer-info": {
      is: RevisionCustomerInfoForm,
      props: curationFormProps,
    },
    "distribution-tags": {
      is: RevisionDistributionTagsForm,
      props: curationFormProps,
    },
    "option-names": {
      is: RevisionOptionNamesForm,
      props: curationFormProps,
    },
  };

  // Dynamic operational sections — one form per operational translation
  if (values?.operationalTranslations?.length) {
    for (const translation of values.operationalTranslations) {
      forms[`operational-${translation.operational_id}`] = {
        is: RevisionOperationalForm,
        props: curationFormProps,
      };
    }
  }

  return forms;
}

export function getCurationSections(baseUrl: string, values: ExperienceRevision, options: RevisionOptions) {
  const sections: SidebarSections = {
    settings: getSettingsSection(baseUrl, values),
    location: getLocationSection(baseUrl, values),
    "content-segmentation": getContentSegmentationSection(baseUrl, values, options),
    "customer-info": getCustomerInfoSection(baseUrl, values),
    "distribution-tags": getDistributionTagsSection(baseUrl, values),
    "option-names": getOptionNamesSection(baseUrl, values),
    ...getOperationalSections(baseUrl, values),
  };

  return mapSidebarSections(sections);
}

function getSettingsSection(baseUrl: string, values?: ExperienceRevision): SidebarCategory {
  return {
    url: `${baseUrl}/settings`,
    icon: "settings" as Icon,
    fields: [
      {
        id: "title",
        required: true,
        filled: !!values?.title,
      },
      {
        id: "supplier_name",
        required: true,
        filled: !!values?.supplierName,
      },
      {
        id: "external_reference_code",
        required: false,
        filled: !!values?.externalReferenceCode,
      },
      {
        id: "seo_title",
        required: false,
        filled: !!values?.seoTitle,
      },
      {
        id: "categories",
        required: false,
        filled: !!((values?.experienceCategory?.length ?? 0) > 0 && (values?.experienceInterest?.length ?? 0) > 0),
      },

      {
        id: "promotional_options",
        required: false,
        filled: !!values?.promotionalOption,
      },
      {
        id: "product_brand",
        required: false,
        filled: !!values?.productBrand,
      },
      {
        id: "own_offer",
        required: false,
        filled: !!values?.ownOffer,
      },
    ],
  };
}

function getLocationSection(baseUrl: string, values?: ExperienceRevision): SidebarCategory {
  return {
    url: `${baseUrl}/location`,
    icon: "location" as Icon,
    fields: [
      {
        id: "location.city",
        required: true,
        filled: !!values?.location?.address.city,
      },
      {
        id: "location.additional-cities",
        required: false,
        filled: !!values?.additionalCities?.length,
      },
      {
        id: "location.venues",
        required: false,
        filled: !!values?.venues?.length,
      },
      {
        id: "location.address",
        required: false,
        filled: !!values?.location?.address.direction,
      },
      {
        id: "location.meeting-point",
        required: false,
        filled: !!values?.meetingPointDetails,
      },
    ],
  };
}

function getContentSegmentationSection(
  baseUrl: string,
  values?: ExperienceRevision,
  options?: RevisionOptions
): SidebarCategory {
  return {
    url: `${baseUrl}/content-segmentation`,
    icon: "content-generation" as Icon,
    fields: [
      {
        id: "description",
        required: true,
        filled: !!values?.description,
      },
      {
        id: "seo_description",
        required: true,
        filled: !!values?.seoDescription,
      },
      {
        id: "additional_description",
        required: false,
        filled: !!values?.additionalDescription,
        hide: !(options?.showAdditionalDescription && useFeatureFlag("showAdditionalDescription")),
      },
      {
        id: "features",
        required: false,
        filled: !!values?.features?.length,
      },
      {
        id: "highlights",
        required: true,
        filled: !!values?.highlights?.custom || !!values?.highlights?.premade,
      },
      {
        id: "included",
        required: true,
        filled: !!values?.included?.custom || !!values?.included?.premade,
      },
      {
        id: "non_included",
        required: false,
        filled: !!values?.nonIncluded?.custom || !!values?.nonIncluded?.premade,
      },
      {
        id: "important_information",
        required: false,
        filled: !!values?.importantInformation?.custom || !!values?.importantInformation?.premade,
      },
      {
        id: "duration",
        required: true,
        filled: !!values?.duration,
      },
    ],
  };
}

function getCustomerInfoSection(baseUrl: string, values?: ExperienceRevision): SidebarCategory {
  return {
    url: `${baseUrl}/customer-info`,
    icon: "customer-info",
    fields: [
      {
        id: "emergency_contact",
        required: false,
        filled: !!values?.emergencyContact?.number,
      },
      {
        id: "voucher_type",
        required: true,
        filled: !!values?.voucherType,
      },
      {
        id: "info_voucher",
        required: false,
        filled: !!values?.infoVoucher,
      },
    ],
  };
}

function getDistributionTagsSection(baseUrl: string, values?: ExperienceRevision): SidebarCategory {
  return {
    url: `${baseUrl}/distribution-tags`,
    icon: "tag" as Icon,
    fields: [
      {
        id: "animal_welfare_tags",
        required: false,
        filled: !!values?.animalWelfareTags?.length,
      },
      {
        id: "health_and_safety_tags",
        required: false,
        filled: !!values?.healthAndSafetyTags?.length,
      },
      {
        id: "other_tags",
        required: false,
        filled: !!values?.otherTags?.length,
      },
    ],
  };
}

function getOperationalSections(baseUrl: string, values?: ExperienceRevision): SidebarSections {
  const sections: SidebarSections = {};

  if (!values?.operationalTranslations?.length) {
    return sections;
  }

  for (const translation of values.operationalTranslations) {
    const sectionKey = `operational-${translation.operational_id}`;
    const relatedOptionNames = values.optionNames?.filter((opt) => opt.operational_id === translation.operational_id);

    sections[sectionKey] = {
      url: `${baseUrl}/operational-${translation.operational_id}`,
      label: translation.code,
      icon: "calendar" as Icon,
      noDropdown: true,
      fields: [
        {
          id: `operational-title-${translation.operational_id}`,
          required: false,
          filled: !!translation.name,
        },
        {
          id: `operational-option-names-${translation.operational_id}`,
          required: false,
          filled: !!relatedOptionNames?.length,
        },
      ],
    };
  }

  return sections;
}

function getOptionNamesSection(baseUrl: string, values?: ExperienceRevision): SidebarCategory {
  return {
    url: `${baseUrl}/option-names`,
    icon: "options" as Icon,
    noDropdown: true,
    hide: !values?.optionNames?.length,
    fields: [
      {
        id: "option-names",
        required: false,
        filled: !!values?.optionNames?.length,
      },
    ],
  };
}
